
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import Button from '../components/Button';
import { authService } from '../services/authService';

interface Domain {
    name: string;
    percentage: number;
}

interface Blueprint {
    id: string;
    name: string;
    state: string;
    grade: number;
    subject: string;
    description: string;
    totalQuestions: number;
    timeLimitMinutes: number;
    domains: Domain[];
}

const TestCenterPage: React.FC = () => {
    const navigate = useNavigate();
    const [blueprints, setBlueprints] = useState<Blueprint[]>([]);
    const [loading, setLoading] = useState(true);
    const user = authService.getCurrentUser();

    // Filters
    const [filterState, setFilterState] = useState(user?.state || 'TX');
    const [filterGrade, setFilterGrade] = useState(user?.grade || 5);

    // Generating State
    const [generatingId, setGeneratingId] = useState<string | null>(null);
    const [topicSelections, setTopicSelections] = useState<Record<string, string>>({});

    useEffect(() => {
        const fetchBlueprints = async () => {
            try {
                // Fetch all blueprints (could optimize to filter serverside, but list is small)
                const data = await api.get('/tests/blueprints');
                setBlueprints(data);
            } catch (err) {
                console.error('Failed to load tests', err);
            } finally {
                setLoading(false);
            }
        };
        fetchBlueprints();
    }, []);

    const handleStartTest = async (blueprint: Blueprint) => {
        setGeneratingId(blueprint.id);
        const topic = topicSelections[blueprint.id] || '';

        try {
            // Generate the test
            const testData = await api.post('/tests/generate', {
                blueprintId: blueprint.id,
                topic
            });

            // Save to local storage for the active test session
            localStorage.setItem('activeTest', JSON.stringify(testData));

            navigate('/test/active');
        } catch (err) {
            alert('Failed to generate test. Please try again.');
        } finally {
            setGeneratingId(null);
        }
    };

    const filteredDefinitions = blueprints.filter(b => {
        // For AP tests, show them for all high school grades (9-12)
        if (b.state === 'AP') {
            return filterGrade >= 9 && filterGrade <= 12;
        }
        // For state tests, match the specific state and grade
        return (b.state === filterState || b.state === 'NATIONAL') && b.grade === filterGrade;
    });

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            <header className="mb-8">
                <h1 className="text-3xl font-bold text-slate-900 mb-2">
                    <i className="fas fa-university mr-3 text-indigo-600"></i>
                    Standardized Test Center
                </h1>
                <p className="text-slate-600">Practice with full-length exams modeled after real state and national standards.</p>
            </header>

            {/* Filters */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex gap-4 mb-8 items-center flex-wrap">
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">State Standard</label>
                    <select
                        value={filterState}
                        onChange={e => setFilterState(e.target.value)}
                        className="px-4 py-2 rounded-lg border border-slate-200 font-bold text-slate-700 outline-none focus:border-indigo-500"
                    >
                        <option value="TX">Texas (STAAR)</option>
                        <option value="CA">California (CAASPP)</option>
                        <option value="FL">Florida (FAST)</option>
                        <option value="NY">New York (Regents)</option>
                        <option value="AP">Advanced Placement (AP)</option>
                        <option value="NATIONAL">National / Common Core</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Grade Level</label>
                    <select
                        value={filterGrade}
                        onChange={e => setFilterGrade(Number(e.target.value))}
                        className="px-4 py-2 rounded-lg border border-slate-200 font-bold text-slate-700 outline-none focus:border-indigo-500"
                    >
                        {[3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(g => <option key={g} value={g}>Grade {g}</option>)}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-12 text-slate-400">Loading assessments...</div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredDefinitions.length === 0 ? (
                        <div className="col-span-full text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                            <p className="text-slate-500 font-bold">No practice tests found for this combination.</p>
                            <p className="text-sm text-slate-400 mt-2">Try selecting "National" or a different grade.</p>
                        </div>
                    ) : filteredDefinitions.map(b => (
                        <div key={b.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow flex flex-col">
                            <div className="flex justify-between items-start mb-4">
                                <div className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider">
                                    {b.state === 'NATIONAL' ? 'National' : `${b.state} State`}
                                </div>
                                <div className="text-slate-400 text-sm font-bold">Grade {b.grade}</div>
                            </div>

                            <h3 className="text-xl font-bold text-slate-800 mb-2">{b.name}</h3>
                            <p className="text-slate-500 text-sm mb-6 flex-grow">{b.description}</p>

                            <div className="border-t border-slate-50 pt-4 mt-4 space-y-3">
                                {b.totalQuestions > 0 ? (
                                    <>
                                        {/* Topic Selection for AP/Standard Tests */}
                                        {b.domains && b.domains.length > 0 && (
                                            <div className="mb-2">
                                                <select
                                                    className="w-full text-sm px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 text-slate-700"
                                                    value={topicSelections[b.id] || ''}
                                                    onChange={(e) => setTopicSelections(prev => ({ ...prev, [b.id]: e.target.value }))}
                                                >
                                                    <option value="">Full Practice Test (Mixed)</option>
                                                    {b.domains.map((d, idx) => (
                                                        <option key={idx} value={d.name}>{d.name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <div className="flex items-center text-sm text-slate-600">
                                            <i className="fas fa-list-ol w-6 text-center text-slate-400"></i>
                                            <span>{b.totalQuestions} Questions</span>
                                        </div>
                                        <div className="flex items-center text-sm text-slate-600">
                                            <i className="fas fa-clock w-6 text-center text-slate-400"></i>
                                            <span>~{b.timeLimitMinutes} Minutes</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex items-center text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                                        <i className="fas fa-palette w-6 text-center"></i>
                                        <span className="font-semibold">Portfolio-Based Course</span>
                                    </div>
                                )}
                            </div>

                            <div className="mt-6">
                                {b.totalQuestions > 0 ? (
                                    <Button
                                        className="w-full justify-center"
                                        onClick={() => handleStartTest(b)}
                                        disabled={generatingId !== null}
                                    >
                                        {generatingId === b.id ? (
                                            <><i className="fas fa-circle-notch fa-spin mr-2"></i> Generating Exam...</>
                                        ) : (
                                            <>Take Practice Test <i className="fas fa-arrow-right ml-2"></i></>
                                        )}
                                    </Button>
                                ) : (
                                    <div className="w-full text-center py-3 px-4 bg-slate-100 text-slate-500 rounded-lg text-sm font-semibold">
                                        No Multiple-Choice Test Available
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )
            }
        </div >
    );
};

export default TestCenterPage;
